import { ChevronDown } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';

import type { StorefrontMenuItem } from '@/features/storefront/api/storefront';
import { useShopContent } from '@/features/storefront/content/useContent';
import { MenuLink } from '@/features/storefront/layouts/chrome/ChromeParts';
import { useHoverDisclosure } from '@/features/storefront/layouts/chrome/MegaMenu';
import { useFittingCount } from '@/features/storefront/layouts/chrome/useFittingCount';
import { cn } from '@/lib/cn';

/**
 * The items there is no room for, under one "More" item at the end of the bar.
 *
 * A card like a flat item's, listing each leftover item with whatever is filed
 * under it, indented - a shortcut, as every panel on a bar is; the shop's full
 * tree is still in the menu sheet and on the category pages.
 */
export function MoreItem({
  items,
  preview,
  triggerClassName,
  panelStyle,
}: {
  items: StorefrontMenuItem[];
  preview?: boolean;
  triggerClassName?: string;
  panelStyle?: CSSProperties;
}) {
  const copy = useShopContent();
  const { open, props } = useHoverDisclosure();
  return (
    <div className="relative" {...props}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        className={cn('flex h-full items-center gap-1 py-2', triggerClassName)}
      >
        {copy('header.more')}
        <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open ? (
        <div className="absolute end-0 top-full z-40 pt-2">
          <div
            className="min-w-60 border py-2 shadow-xl"
            style={{
              backgroundColor: 'var(--storefront-background)',
              borderColor: 'var(--storefront-surface)',
              ...panelStyle,
            }}
          >
            {items.map((item) => (
              <div key={`${item.label}:${item.url}`}>
                <MenuLink item={item} preview={preview} className="block px-5 py-2 hover:opacity-60" />
                {(item.items ?? []).map((child) => (
                  <MenuLink
                    key={`${child.label}:${child.url}`}
                    item={child}
                    preview={preview}
                    className="block py-1.5 ps-9 pe-5 text-[14px] hover:opacity-60"
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export interface FittingNavProps {
  items: StorefrontMenuItem[];
  label?: string;
  preview?: boolean;
  /** The bar's voice - gap, size, tracking - worn by the bar and its measuring copy alike. */
  navClassName: string;
  /** Draws one item as the family does: a plain link, or a link that opens its shelves. */
  renderItem: (item: StorefrontMenuItem) => ReactNode;
  /** The gap between an item with children and its chevron, as `renderItem` draws it. */
  chevronGapClassName?: string;
  moreClassName?: string;
  className?: string;
}

/**
 * A header bar that never spills: as many items as fit between the wordmark
 * and the shopper's corner, the rest under "More" (`useFittingCount`).
 *
 * The invisible copy beside it is what is measured - every label in the same
 * type, a chevron's room for each item with children, and "More" itself - so
 * the answer holds for whatever a merchant names their shelves.
 */
export function FittingNav({
  items,
  label,
  preview,
  navClassName,
  renderItem,
  chevronGapClassName = 'gap-1',
  moreClassName,
  className,
}: FittingNavProps) {
  const copy = useShopContent();
  const { space, measure, count } = useFittingCount(items.length);
  const shown = items.slice(0, count);
  const rest = items.slice(count);
  return (
    <div ref={space} className={cn('relative flex min-w-0 flex-1 justify-center', className)}>
      <nav aria-label={label} className={cn('flex', navClassName)}>
        {shown.map((item) => renderItem(item))}
        {rest.length > 0 ? <MoreItem items={rest} preview={preview} triggerClassName={moreClassName} /> : null}
      </nav>
      {/* Clipped to the bar: the copy is as wide as every label at once, and
          left unclipped it pushed the whole page sideways past the window. */}
      <div aria-hidden className="pointer-events-none invisible absolute inset-0 overflow-hidden">
        <div ref={measure} className={cn('flex w-max', navClassName)}>
          {items.map((item) => (
            <span key={`${item.label}:${item.url}`} className={cn('flex items-center', chevronGapClassName)}>
              {item.label}
              {(item.items ?? []).length > 0 ? <span className="size-3.5" /> : null}
            </span>
          ))}
          {/* A button, as the real "More" is, so it measures in the same type. */}
          <button type="button" tabIndex={-1} className={cn('flex items-center gap-1', moreClassName)}>
            {copy('header.more')}
            <span className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
